import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import type { ReactNode } from 'react';
import { flushSync } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { authApi } from '../api/authApi';
import { bumpAuthWriteGeneration } from '../api/authSessionGuard';
import { userApi } from '../api/userApi';
import type { User, UserProfileRow, ImpersonationState } from '../api/types';
import { extractAndValidateUser } from '../utils/userHelpers';
import { TOKEN_KEY, REFRESH_TOKEN_KEY, USER_KEY } from '../config/api';
import { showSuccessToast } from '../utils/toast';
import { disconnectGlobalSocket } from '../hooks/useSocket';
import { MA_ACTIVE_TEAM_ID_KEY } from '../constants/teamStorage';

interface AuthContextType {
  user: User | null;
  impersonation: ImpersonationState | null;
  isImpersonating: boolean;
  isAuthenticated: boolean;
  isLoading: boolean;
  setAuthFromTokens: (data: {
    accessToken?: string;
    refreshToken?: string;
    user?: User;
    token?: string;
  }) => void;
  login: (
    email: string,
    password: string,
  ) => Promise<{
    email_verification_required?: boolean;
    phone_verification_required?: boolean;
    user?: User;
  }>;
  logout: () => void;
  verifyEmail: (
    emailornumber: string,
    otp: string,
    is_mobile: boolean,
  ) => Promise<void>;
  verifyPhone: (firebaseToken: string) => Promise<{
    email_verification_required?: boolean;
    basic_profile_required?: boolean;
  }>;
  googleLogin: (idToken: string) => Promise<{
    phone_verification_required?: boolean;
    user?: User;
  }>;
  sendPhoneOTP: (userId?: number) => Promise<void>;
  getRoleBasedRedirect: (role?: string, activeProfileType?: string) => string;
  isAdmin: () => boolean;
  /**
   * Current **session** is provider: `active_profile_type === 'doctor'`.
   * Use for shell/nav that should only show while acting as a healthcare provider.
   */
  isDoctor: () => boolean;
  /**
   * Account **entitlement**: `user.role === 'doctor'` (signup/onboarding, doctor gates).
   * Not the same as `isDoctor()` — a doctor account can still have active profile `patient` until switch.
   */
  isDoctorAccount: () => boolean;
  isUser: () => boolean;
  isPatient: () => boolean;
  switchProfile: (type: 'doctor' | 'patient') => Promise<void>;
  addDoctorProfile: () => Promise<void>;
  startImpersonation: (payload: {
    targetUserId: number | string;
    supportRequestId?: number | string;
    reason?: string;
    ticketId?: string;
    scope?: 'read_only' | 'support_write';
  }) => Promise<void>;
  stopImpersonation: () => Promise<void>;
  availableProfiles: UserProfileRow[];
  refreshUser: () => Promise<void>;
  /** True while Google OAuth is in progress (Login/Signup); disables global header. */
  googleOAuthInProgress: boolean;
  setGoogleOAuthInProgress: (value: boolean) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const clearIdentityScopedCaches = () => {
  try {
    localStorage.removeItem(MA_ACTIVE_TEAM_ID_KEY);
    localStorage.removeItem('liked_recipes');
    localStorage.removeItem('saved_recipes');
    localStorage.removeItem('recipe_collections');
    // Clear lingering invite tokens so they don't apply to the next session
    localStorage.removeItem('ma_team_invite_token_v1');
    localStorage.removeItem('ma_family_invite_token_v1');

    // Clear dismissable banners/notices and identity-tied drafts.
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;
      if (
        key.includes('dismiss_') ||
        key.includes('ownership_') ||
        key.includes('accepted_') ||
        key.startsWith('ayurved_recipe_draft_') ||
        key.startsWith('ma_rec_eng_v1_')
      ) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));
  } catch {
    // ignore
  }
  try {
    sessionStorage.removeItem('signup_user_id');
    sessionStorage.removeItem('signup_form_draft');
    sessionStorage.removeItem('signup_step');
  } catch {
    // ignore
  }
  // Force a fresh socket for the next identity (impersonation / stop / logout).
  disconnectGlobalSocket();
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(() => {
    // Initialize user from localStorage on mount (sync, before first render)
    try {
      const token = localStorage.getItem('token');
      const userStr = localStorage.getItem('user');
      if (token && userStr) {
        const parsedUser = JSON.parse(userStr);
        console.log('🚀 Initial user from localStorage:', parsedUser.role);
        return parsedUser;
      }
    } catch (error) {
      console.error('Failed to parse initial user:', error);
    }
    return null;
  });
  const [isLoading, setIsLoading] = useState(true);
  const [googleOAuthInProgress, setGoogleOAuthInProgress] = useState(false);
  const [impersonation, setImpersonation] = useState<ImpersonationState | null>(
    user?.impersonation || null,
  );
  /** Bumped on impersonation start/stop so in-flight refreshUser cannot overwrite the new session. */
  const authEpochRef = useRef(0);

  const getRoleBasedRedirect = useCallback(
    (roleArg?: string, activeArg?: string) => {
      const role = roleArg ?? user?.role;
      const rawApt = activeArg ?? user?.active_profile_type;
      const apt = String(rawApt || '').toLowerCase();
      if (role === 'admin') return '/admin/dashboard';
      // A doctor account with no profile flag yet is still the provider session.
      // Treating that gap as "patient" sends the first impersonation load to the
      // public home, which looks like access was denied.
      if (apt === 'doctor' || (role === 'doctor' && apt === '')) {
        return '/doctor/dashboard';
      }
      if (apt === 'patient') return '/';
      if (role === 'doctor') return '/doctor/dashboard';
      return '/';
    },
    [user],
  );

  const setAuthFromTokens: AuthContextType['setAuthFromTokens'] = useCallback(
    (data) => {
      const token = data.token || data.accessToken;
      if (token) localStorage.setItem(TOKEN_KEY, token);
      if (data.refreshToken)
        localStorage.setItem(REFRESH_TOKEN_KEY, data.refreshToken);
      if (data.user) {
        localStorage.setItem(USER_KEY, JSON.stringify(data.user));
        setUser(data.user);
        setImpersonation(data.user.impersonation || null);
      }
    },
    [],
  );

  // Initialize auth state
  useEffect(() => {
    const initAuth = async () => {
      console.log('🔄 [AUTH INIT] Starting auth initialization...');
      console.log(
        '🔄 [AUTH INIT] User from initial state:',
        user ? { email: user.email, role: user.role } : 'null',
      );

      // If we already have user from initial state, validate against the API
      // before unlocking the UI. Keeping isLoading=true until the refresh
      // finishes prevents any component (e.g. ProfileCompletionGate) from
      // rendering with a stale cached user and showing the wrong UI for a frame.
      if (user) {
        console.log(
          '🔄 [AUTH INIT] User exists, refreshing from API before unlocking UI',
        );

        try {
          console.log(
            '🔄 [AUTH INIT] Calling userApi.getProfile() to refresh user data...',
          );
          const response = await Promise.race([
            userApi.getProfile(),
            new Promise<never>((_, reject) =>
              setTimeout(
                () => reject(new Error('Auth profile refresh timed out')),
                15000,
              ),
            ),
          ]);
          console.log('🔄 [AUTH INIT] Profile refresh response:', {
            success: response.success,
            hasData: !!response.data,
          });
          const userData = extractAndValidateUser(response);
          if (userData) {
            console.log('✅ [AUTH INIT] Refreshed user data from API:', {
              email: userData.email,
              role: userData.role,
            });
            setUser(userData);
            localStorage.setItem('user', JSON.stringify(userData));
            setImpersonation(userData.impersonation || null);
          } else {
            console.warn(
              '⚠️ [AUTH INIT] API response missing required user fields (email/role), keeping cached user',
            );
          }
        } catch (refreshError) {
          console.warn(
            '⚠️ [AUTH INIT] Exception during refresh, keeping cached user:',
            refreshError,
          );
        } finally {
          setIsLoading(false);
        }
        console.log('🔄 [AUTH INIT] Initialization complete (user existed)');
        return;
      }

      // No user in initial state, check localStorage again
      try {
        const token = authApi.getToken();
        const storedUser = authApi.getUser();

        console.log('📦 [AUTH INIT] Checking localStorage:', {
          hasToken: !!token,
          hasUser: !!storedUser,
        });
        if (storedUser) {
          console.log('📦 [AUTH INIT] Stored user:', {
            email: storedUser.email,
            role: storedUser.role,
          });
        }

        if (token && storedUser) {
          console.log(
            '✅ [AUTH INIT] Token and user found, refreshing from API before unlocking UI',
          );
          setUser(storedUser);

          // Refresh before releasing isLoading so consumers always see fresh data.
          try {
            console.log(
              '🔄 [AUTH INIT] Calling userApi.getProfile() to refresh...',
            );
            const response = await Promise.race([
              userApi.getProfile(),
              new Promise<never>((_, reject) =>
                setTimeout(
                  () => reject(new Error('Auth profile refresh timed out')),
                  15000,
                ),
              ),
            ]);
            console.log('🔄 [AUTH INIT] Refresh response:', {
              success: response.success,
              hasData: !!response.data,
            });
            const userData = extractAndValidateUser(response);
            if (userData) {
              console.log('✅ [AUTH INIT] User refreshed successfully:', {
                email: userData.email,
                role: userData.role,
              });
              setUser(userData);
              localStorage.setItem('user', JSON.stringify(userData));
              setImpersonation(userData.impersonation || null);
            } else {
              console.warn(
                '⚠️ [AUTH INIT] API response missing required user fields (email/role), keeping cached user',
              );
            }
          } catch (refreshError) {
            console.warn(
              '⚠️ [AUTH INIT] Exception during refresh, keeping cached user:',
              refreshError,
            );
          } finally {
            setIsLoading(false);
          }
        } else if (token && !storedUser) {
          console.log(
            '🔄 [AUTH INIT] Token exists but no user, fetching from API...',
          );
          // Have token but no user - fetch from API
          try {
            const response = await userApi.getProfile();
            const userData = extractAndValidateUser(response);
            if (userData) {
              console.log('✅ [AUTH INIT] Fetched user from API:', {
                email: userData.email,
                role: userData.role,
              });
              setUser(userData);
              localStorage.setItem('user', JSON.stringify(userData));
              setImpersonation(userData.impersonation || null);
            } else {
              console.error(
                '❌ [AUTH INIT] Failed to fetch valid user data, logging out',
              );
              authApi.logout();
              setUser(null);
              setImpersonation(null);
            }
          } catch (error) {
            console.error(
              '❌ [AUTH INIT] Exception fetching user, logging out:',
              error,
            );
            authApi.logout();
            setUser(null);
            setImpersonation(null);
          }
          setIsLoading(false);
        } else {
          console.log('❌ [AUTH INIT] No auth data found, logging out');
          // No auth data
          authApi.logout();
          setUser(null);
          setImpersonation(null);
          setIsLoading(false);
        }
      } catch (error) {
        console.error(
          '❌ [AUTH INIT] Exception during initialization, logging out:',
          error,
        );
        authApi.logout();
        setUser(null);
        setImpersonation(null);
        setIsLoading(false);
      }
      console.log('🔄 [AUTH INIT] Initialization complete');
    };

    initAuth();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const login = async (email: string, password: string) => {
    authEpochRef.current += 1;
    const response = await authApi.login({ email, password });
    if (!response.success) {
      const err = new Error(response.error || 'Login failed') as Error & {
        validation?: Record<string, string | string[]>;
      };
      err.validation = response.data?.validation;
      throw err;
    }
    if (response.data?.phone_verification_required) {
      return {
        phone_verification_required: true,
        user: response.data.user,
      };
    }
    if (response.data?.email_verification_required) {
      return { email_verification_required: true };
    }
    // Single profile write: avoid setUser(loginPayload) then setUser(getProfile) — that
    // double context update makes UniversalSignup / gates re-render twice (visible blink).
    if (response.data?.user && authApi.isAuthenticated()) {
      try {
        const userResponse = await userApi.getProfile();
        const userData = extractAndValidateUser(userResponse);
        if (userData) {
          setUser(userData);
          localStorage.setItem('user', JSON.stringify(userData));
          setImpersonation(userData.impersonation || null);
        } else {
          setUser(response.data.user);
          localStorage.setItem('user', JSON.stringify(response.data.user));
          setImpersonation(response.data.user.impersonation || null);
        }
      } catch (error) {
        console.warn('Failed to refresh profile after login:', error);
        setUser(response.data.user);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        setImpersonation(response.data.user.impersonation || null);
      }
    } else if (response.data?.user) {
      setUser(response.data.user);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      setImpersonation(response.data.user.impersonation || null);
    } else if (authApi.isAuthenticated()) {
      try {
        const userResponse = await userApi.getProfile();
        const userData = extractAndValidateUser(userResponse);
        if (userData) {
          setUser(userData);
          localStorage.setItem('user', JSON.stringify(userData));
          setImpersonation(userData.impersonation || null);
        }
      } catch (error) {
        console.error('Failed to fetch user profile after login:', error);
      }
    }
    return { email_verification_required: false };
  };

  const logout = () => {
    // Invalidate in-flight refreshUser / token writes before clearing storage.
    authEpochRef.current += 1;
    clearIdentityScopedCaches();
    // Clear persisted family profile selection so next login starts on guardian/self.
    try {
      const accountId = user?.id != null ? String(user.id) : null;
      if (accountId) {
        localStorage.removeItem(`ma_family_active_profile_v1_${accountId}`);
        localStorage.removeItem(
          `ma_family_active_profile_role_v1_${accountId}`,
        );
      }
    } catch {
      // ignore
    }
    authApi.logout();
    disconnectGlobalSocket();
    setUser(null);
    setImpersonation(null);
    setGoogleOAuthInProgress(false);
    // Clear any persisted pre-auth signup progress so a new user always starts at Step 1.
    try {
      sessionStorage.removeItem('signup_user_id');
      sessionStorage.removeItem('signup_form_draft');
      sessionStorage.removeItem('signup_step');
    } catch {
      // ignore
    }
    // Navigation will be handled by the component calling logout
  };

  const verifyEmail = async (
    emailornumber: string,
    otp: string,
    is_mobile: boolean,
  ) => {
    const response = await authApi.verifyEmail({
      emailornumber,
      otp,
      is_mobile,
    });
    if (!response.success) {
      const err = new Error(
        response.error || 'Verification failed',
      ) as Error & {
        validation?: Record<string, string | string[]>;
      };
      err.validation = response.data?.validation;
      throw err;
    }
    // Tokens are persisted in authApi.verifyEmail — load full profile so `profiles` /
    // `doctor_status` match GET /user/profile (same as password login).
    if (authApi.isAuthenticated()) {
      try {
        const userResponse = await userApi.getProfile();
        const userData = extractAndValidateUser(userResponse);
        if (userData) {
          setUser(userData);
          localStorage.setItem('user', JSON.stringify(userData));
          setImpersonation(userData.impersonation || null);
        } else if (response.data?.user) {
          setUser(response.data.user);
          localStorage.setItem('user', JSON.stringify(response.data.user));
          setImpersonation(response.data.user.impersonation || null);
        }
      } catch (error) {
        console.error(
          'Failed to fetch user profile after verification:',
          error,
        );
        if (response.data?.user) {
          setUser(response.data.user);
          localStorage.setItem('user', JSON.stringify(response.data.user));
          setImpersonation(response.data.user.impersonation || null);
        }
      }
    } else if (response.data?.user) {
      setUser(response.data.user);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      setImpersonation(response.data.user.impersonation || null);
    }
  };

  const verifyPhone = async (firebaseToken: string) => {
    const response = await authApi.verifyPhone({ firebaseToken });
    if (!response.success) {
      throw new Error(response.error || 'Phone verification failed');
    }
    // persistAuthData in authApi.verifyPhone — full profile so `profiles` / doctor_status load.
    if (authApi.isAuthenticated()) {
      try {
        const userResponse = await userApi.getProfile();
        const userData = extractAndValidateUser(userResponse);
        if (userData) {
          setUser(userData);
          localStorage.setItem('user', JSON.stringify(userData));
          setImpersonation(userData.impersonation || null);
        } else if (response.data?.user) {
          setUser(response.data.user);
          localStorage.setItem('user', JSON.stringify(response.data.user));
          setImpersonation(response.data.user.impersonation || null);
        }
      } catch (error) {
        console.warn(
          'Failed to refresh user profile after phone verification:',
          error,
        );
        if (response.data?.user) {
          setUser(response.data.user);
          localStorage.setItem('user', JSON.stringify(response.data.user));
          setImpersonation(response.data.user.impersonation || null);
        }
      }
    } else if (response.data?.user) {
      setUser(response.data.user);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      setImpersonation(response.data.user.impersonation || null);
    }
    return {
      email_verification_required: response.data?.email_verification_required,
      basic_profile_required: response.data?.basic_profile_required,
    };
  };

  const googleLogin = async (idToken: string) => {
    const response = await authApi.googleLogin({ id_token: idToken });
    if (!response.success) {
      throw new Error(response.error || 'Google login failed');
    }
    // Single profile write (same rationale as email/password login).
    if (response.data?.user && authApi.isAuthenticated()) {
      try {
        const userResponse = await userApi.getProfile();
        const userData = extractAndValidateUser(userResponse);
        if (userData) {
          setUser(userData);
          localStorage.setItem('user', JSON.stringify(userData));
          setImpersonation(userData.impersonation || null);
        } else {
          setUser(response.data.user);
          localStorage.setItem('user', JSON.stringify(response.data.user));
          setImpersonation(response.data.user.impersonation || null);
        }
      } catch (error) {
        console.error(
          'Failed to fetch user profile after Google login:',
          error,
        );
        setUser(response.data.user);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        setImpersonation(response.data.user.impersonation || null);
      }
    } else if (response.data?.user) {
      setUser(response.data.user);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      setImpersonation(response.data.user.impersonation || null);
    } else if (authApi.isAuthenticated()) {
      try {
        const userResponse = await userApi.getProfile();
        const userData = extractAndValidateUser(userResponse);
        if (userData) {
          setUser(userData);
          localStorage.setItem('user', JSON.stringify(userData));
          setImpersonation(userData.impersonation || null);
        }
      } catch (error) {
        console.error(
          'Failed to fetch user profile after Google login:',
          error,
        );
      }
    }
    // Return phone verification flag + user (for phone modal prefill)
    return {
      phone_verification_required:
        response.data?.phone_verification_required || false,
      user: response.data?.user,
    };
  };

  const sendPhoneOTP = async (userId?: number) => {
    const response = await authApi.sendPhoneOTP(userId);
    if (!response.success) {
      throw new Error(response.error || 'Failed to send OTP');
    }
  };

  // Role helper functions
  const isAdmin = (): boolean => {
    return user?.role === 'admin';
  };

  const isDoctor = useCallback((): boolean => {
    return user?.active_profile_type === 'doctor';
  }, [user]);

  const isDoctorAccount = useCallback((): boolean => {
    return user?.role === 'doctor';
  }, [user]);

  const isPatient = (): boolean => {
    return !user?.active_profile_type || user.active_profile_type === 'patient';
  };

  const isUser = (): boolean => isPatient();

  const availableProfiles: UserProfileRow[] = user?.profiles ?? [];

  const refreshUser = useCallback(async () => {
    if (!authApi.isAuthenticated()) return;
    const epoch = authEpochRef.current;
    try {
      const rt =
        typeof window !== 'undefined'
          ? localStorage.getItem(REFRESH_TOKEN_KEY)
          : null;
      if (rt) {
        const ref = await authApi.refreshToken(rt);
        if (!ref.success) {
          // Expired or invalid refresh — still try GET /users/me with current access token
        }
      }
      // Drop stale responses after impersonation start/stop (or any epoch bump).
      if (epoch !== authEpochRef.current) return;
      const response = await userApi.getProfile();
      if (epoch !== authEpochRef.current) return;
      const userData = extractAndValidateUser(response);
      if (userData && epoch === authEpochRef.current) {
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
        setImpersonation(userData.impersonation || null);
      }
    } catch (error) {
      console.warn('Failed to refresh user profile:', error);
    }
  }, []);

  const switchProfile = async (type: 'doctor' | 'patient') => {
    const wasImpersonating = !!impersonation?.isImpersonating;
    authEpochRef.current += 1;
    const epoch = authEpochRef.current;
    bumpAuthWriteGeneration();
    const response = await authApi.switchProfile(type);
    if (!response.success || !response.data) {
      throw new Error(response.error || 'Could not switch profile');
    }
    if (epoch !== authEpochRef.current) return;
    setAuthFromTokens({
      accessToken: response.data.accessToken,
      refreshToken: response.data.refreshToken,
    });
    // `setUser` from refresh is async-batched; navigating immediately leaves RoleProtectedRoute
    // seeing stale `active_profile_type` (patient) and redirecting away from /doctor/*.
    const profileResponse = await userApi.getProfile();
    if (epoch !== authEpochRef.current) return;
    const switchedUser = extractAndValidateUser(profileResponse);
    if (switchedUser) {
      if (
        wasImpersonating &&
        switchedUser.impersonation?.isImpersonating !== true
      ) {
        throw new Error(
          'Profile switch cleared impersonation session. Please stop and restart support access.',
        );
      }
      localStorage.setItem(USER_KEY, JSON.stringify(switchedUser));
      flushSync(() => {
        setUser(switchedUser);
        setImpersonation(switchedUser.impersonation || null);
      });
    } else {
      await refreshUser();
    }
    navigate(type === 'doctor' ? '/doctor/dashboard' : '/');
  };

  const addDoctorProfile = useCallback(async () => {
    const response = await authApi.addDoctorProfile();
    if (!response.success) {
      const again = await authApi.getMyProfiles();
      if (
        again.success &&
        again.data?.profiles?.some((p) => p.profile_type === 'doctor')
      ) {
        await refreshUser();
        showSuccessToast(
          'You already have a doctor profile. Use Switch to Doctor in the profile menu to continue setup or submit for review.',
        );
        return;
      }
      throw new Error(
        (response.data as { message?: string } | undefined)?.message ||
          response.error ||
          'Could not add doctor profile',
      );
    }
    try {
      const rt =
        typeof window !== 'undefined'
          ? localStorage.getItem(REFRESH_TOKEN_KEY)
          : null;
      if (rt) {
        const ref = await authApi.refreshToken(rt);
        if (ref.success && ref.data?.accessToken) {
          setAuthFromTokens({
            accessToken: ref.data.accessToken,
            refreshToken: ref.data.refreshToken,
          });
        }
      }
    } catch {
      // non-fatal — session still valid with old tokens
    }
    await refreshUser();
    showSuccessToast(
      'Provider profile added. Use Switch to Doctor to complete your details and submit them for review when you are ready — same verification flow as other doctors.',
    );
  }, [refreshUser, setAuthFromTokens]);

  const startImpersonation: AuthContextType['startImpersonation'] = useCallback(
    async ({
      targetUserId,
      supportRequestId,
      reason,
      ticketId,
      scope = 'support_write',
    }) => {
      authEpochRef.current += 1;
      const epoch = authEpochRef.current;
      bumpAuthWriteGeneration();
      setIsLoading(true);
      try {
        const response = await authApi.startImpersonation({
          target_user_id: targetUserId,
          support_request_id: supportRequestId,
          reason,
          ticket_id: ticketId,
          scope,
        });
        if (!response.success || !response.data) {
          throw new Error(response.error || 'Could not start impersonation');
        }
        clearIdentityScopedCaches();
        setAuthFromTokens({
          token: response.data.token || response.data.accessToken,
          refreshToken: response.data.refreshToken,
          user: response.data.user,
        });

        // Commit full profile before remount so the next boot has the doctor/patient user.
        const profileResponse = await userApi.getProfile();
        if (epoch !== authEpochRef.current) return;
        const impersonatedUser = extractAndValidateUser(profileResponse);
        const startedUser = response.data.user;
        const effectiveUser = impersonatedUser || startedUser;
        if (effectiveUser && startedUser) {
          if (
            !effectiveUser.active_profile_type &&
            startedUser.active_profile_type
          ) {
            effectiveUser.active_profile_type = startedUser.active_profile_type;
          }
          if (!effectiveUser.impersonation && startedUser.impersonation) {
            effectiveUser.impersonation = startedUser.impersonation;
          }
          if (
            !effectiveUser.active_profile_type &&
            String(effectiveUser.role || '').toLowerCase() === 'doctor'
          ) {
            effectiveUser.active_profile_type = 'doctor';
          }
        }
        if (effectiveUser) {
          localStorage.setItem(USER_KEY, JSON.stringify(effectiveUser));
          flushSync(() => {
            setUser(effectiveUser);
            setImpersonation(effectiveUser.impersonation || null);
          });
        } else {
          await refreshUser();
        }

        // Full remount = exact copy of that user's app (providers, lists, sockets).
        // Soft navigate() leaves the admin shell mounted and was the root of stale teams/UI.
        const role = String(effectiveUser?.role || '').toLowerCase();
        const apt = String(
          (effectiveUser as { active_profile_type?: string } | null)
            ?.active_profile_type || '',
        ).toLowerCase();
        const homePath =
          role === 'doctor' || apt === 'doctor'
            ? '/doctor/dashboard'
            : role === 'admin'
              ? '/admin/dashboard'
              : '/';
        if (typeof window !== 'undefined') {
          window.location.assign(homePath);
          return;
        }
      } finally {
        if (epoch === authEpochRef.current) setIsLoading(false);
      }
    },
    [refreshUser, setAuthFromTokens],
  );

  const stopImpersonation: AuthContextType['stopImpersonation'] =
    useCallback(async () => {
      authEpochRef.current += 1;
      const epoch = authEpochRef.current;
      bumpAuthWriteGeneration();
      setIsLoading(true);
      try {
        const response = await authApi.stopImpersonation();
        if (!response.success || !response.data) {
          throw new Error(response.error || 'Could not stop impersonation');
        }
        clearIdentityScopedCaches();
        setAuthFromTokens({
          token: response.data.token || response.data.accessToken,
          refreshToken: response.data.refreshToken,
          user: response.data.user,
        });

        const profileResponse = await userApi.getProfile();
        if (epoch !== authEpochRef.current) return;
        const restoredUser = extractAndValidateUser(profileResponse);
        if (restoredUser) {
          localStorage.setItem(USER_KEY, JSON.stringify(restoredUser));
          flushSync(() => {
            setUser(restoredUser);
            setImpersonation(restoredUser.impersonation || null);
          });
        } else {
          await refreshUser();
        }
        window.dispatchEvent(new Event('meraayurved:team-membership-stale'));
        window.dispatchEvent(new Event('meraayurved:identity-changed'));
        // Hard redirect so in-flight doctor layout polls / soft navigates cannot leave
        // the admin stuck on the impersonated user's page after browsing.
        if (typeof window !== 'undefined') {
          window.location.assign('/admin/dashboard');
          return;
        }
      } finally {
        if (epoch === authEpochRef.current) setIsLoading(false);
      }
    }, [refreshUser, setAuthFromTokens]);

  // Family invite tokens live in localStorage (see InviteAcceptPage + UniversalSignup).
  // Accept is handled there after signup completes; do not duplicate here to avoid races.

  return (
    <AuthContext.Provider
      value={{
        user,
        impersonation,
        isImpersonating: !!impersonation?.isImpersonating,
        isAuthenticated: !!user,
        isLoading,
        setAuthFromTokens,
        login,
        logout,
        verifyEmail,
        verifyPhone,
        googleLogin,
        sendPhoneOTP,
        getRoleBasedRedirect,
        isAdmin,
        isDoctor,
        isDoctorAccount,
        isUser,
        isPatient,
        switchProfile,
        addDoctorProfile,
        startImpersonation,
        stopImpersonation,
        availableProfiles,
        refreshUser,
        googleOAuthInProgress,
        setGoogleOAuthInProgress,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error(
      'useAuth must be used within an AuthProvider. Make sure AuthProvider wraps your app.',
    );
  }
  return context;
};
